import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useRealtimeTags } from 'Core/hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { getInitialRange } from '../../../../.globals/helpers';

// Components
import { Loading, Icons } from "@gpstrackit/gtc-react-controls";
import { TagsGroup } from 'Components/Tag';
import { Divider } from '@mui/material';

// Actions
import { openVideoAssignDriverModal } from "Modules/.common/components/VideoAssignDriver/redux/slice";
import { catalogLoad } from 'Redux/actions';
import { getDriver } from './redux/slice';

//Styles
import './index.scss';
const DriverInfo = (props) => {
    const { openTooltip, driverId, driverKey, driverDetailStartDate, driverDetailEndDate, event } = props
    const dispatch = useDispatch();
    const { messages } = useIntl();
    const navigate = useNavigate();
    const location = useLocation();

    // Redux States
    const loading = useSelector((state) => state.driverPerformanceEventsTabDriverInfoRedux.loading);
    const driver = useSelector((state) => state.driverPerformanceEventsTabDriverInfoRedux.driver);
    const tags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] });
    const driverTabDateStart = useSelector((state) => state?.dashboardDriverPerformanceSliceRedux?.filters?.dateStart);
    const driverTabDateEnd = useSelector((state) => state?.dashboardDriverPerformanceSliceRedux?.filters?.dateEnd);
    const casesLastSearchRange = useSelector((state) => state?.driverPerformanceCasesTabRedux?.lastSearchRange);
    const dashboardGlobals = useSelector((state) => state?.driverPerformanceDashboardRedux?.filters?.globals);
    const mainTab = useSelector((state) => state?.driverPerformanceMainRedux?.tab);

    const getReturnTab = () => {
        const urlParams = new URLSearchParams(location.search);
        const tabParam = urlParams.get('tab');
        if (tabParam) return tabParam;
        switch (mainTab) {
            case 0:
                return 'dashboard';
            case 2:
                return 'events';
            case 3:
                return 'cases';
            case 1:
            default:
                return 'drivers';
        }
    };

    const getRangeForDriverDetail = () => {
        if (
            driverDetailStartDate &&
            driverDetailEndDate &&
            moment(driverDetailStartDate).isValid() &&
            moment(driverDetailEndDate).isValid()
        ) {
            return {
                startDate: driverDetailStartDate,
                endDate: driverDetailEndDate
            };
        }

        const urlParams = new URLSearchParams(location.search);

        const tabParam = urlParams.get('tab');
        const isCasesContext =
            tabParam === 'cases' ||
            mainTab === 3 ||
            location.pathname?.includes('/driverPerformance/cases') ||
            location.pathname?.includes('/driverPerformance/caseDetail');

        const isEventsContext = tabParam === 'events' || mainTab === 2;
        const isDriversContext = tabParam === 'drivers' || mainTab === 1;

        if (isCasesContext && casesLastSearchRange?.GTE && casesLastSearchRange?.LTE) {
            const hasValidRange =
                moment(casesLastSearchRange.GTE).isValid() &&
                moment(casesLastSearchRange.LTE).isValid();

            if (hasValidRange) {
                return {
                    startDate: moment.utc(casesLastSearchRange.GTE).startOf('day').toISOString(),
                    endDate: moment.utc(casesLastSearchRange.LTE).endOf('day').toISOString()
                };
            }
        }

        const startParam = isEventsContext
            ? urlParams.get('eventsStartDate')
            : (isDriversContext ? urlParams.get('driversStartDate') : null);
        const endParam = isEventsContext
            ? urlParams.get('eventsEndDate')
            : (isDriversContext ? urlParams.get('driversEndDate') : null);
        const rangeParam = isEventsContext
            ? urlParams.get('eventsRange')
            : (isDriversContext ? urlParams.get('driversRange') : null);

        const hasStartEndParams =
            Boolean(startParam && endParam) &&
            moment(startParam).isValid() &&
            moment(endParam).isValid();

        if (hasStartEndParams) {
            return {
                startDate: moment.utc(startParam).startOf('day').toISOString(),
                endDate: moment.utc(endParam).endOf('day').toISOString()
            };
        }

        if (rangeParam) {
            const range = getInitialRange(rangeParam);
            if (range?.start && range?.end) {
                return {
                    startDate: moment.utc(range.start).startOf('day').toISOString(),
                    endDate: moment.utc(range.end).endOf('day').toISOString()
                };
            }
        }

        if (dashboardGlobals?.timeFrame) {
            const isCustom = dashboardGlobals?.timeFrame === 'custom';

            if (!isCustom) {
                const range = getInitialRange(dashboardGlobals.timeFrame);
                if (range?.start && range?.end) {
                    return {
                        startDate: moment.utc(range.start).startOf('day').toISOString(),
                        endDate: moment.utc(range.end).endOf('day').toISOString()
                    };
                }
            }

            if (isCustom && dashboardGlobals?.rangeDate?.start && dashboardGlobals?.rangeDate?.end) {
                return {
                    startDate: moment.utc(dashboardGlobals.rangeDate.start).startOf('day').toISOString(),
                    endDate: moment.utc(dashboardGlobals.rangeDate.end).endOf('day').toISOString()
                };
            }
        }

        if (driverTabDateStart && driverTabDateEnd) {
            return {
                startDate: moment.utc(driverTabDateStart).startOf('day').toISOString(),
                endDate: moment.utc(driverTabDateEnd).endOf('day').toISOString()
            };
        }

        return {
            startDate: moment.utc().startOf('month').toISOString(),
            endDate: moment.utc().endOf('month').toISOString()
        };
    };

    const buildDriverDetailUrl = (driverIdToOpen) => {
        const { startDate, endDate } = getRangeForDriverDetail();
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        params.append('sourceModule', 'eventsDriverInfo');
        params.append('returnTab', getReturnTab());
        return `/app/driverPerformance/driverDetail/${driverIdToOpen}?${params.toString()}`;
    };

    // Local States
    const [currentTags, setCurrentTags] = useState([]);

    useEffect(() => {
        if (openTooltip && driverId) {
            dispatch(getDriver({ id: driverId, driverKey: driverKey }));
        }
    }, [openTooltip])

    useEffect(() => {
        if (driver) {
            let tagIds = driver?.tagIds || [];
            let current = [];

            if (tagIds === "[-99]") {
                current = tags;
            } else {
                tags?.forEach((tag) => {
                    if (tagIds?.includes(`${tag?.id}`)) {
                        current.push(tag);
                    }
                });
            }
            setCurrentTags(current)
        }
    }, [driver, tags])

    useEffect(() => {
        if (openTooltip && tags.length === 0) {
            dispatch(catalogLoad(['tags']));
        }
    }, [openTooltip])

    return (
        <div style={{ minHeight: 100, width: 249 }}>
            {
                loading ? <div><Loading full /></div > :
                    <>
                        {
                            driver ? (
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 25 }}>
                                        <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_driver_id']}:</strong>
                                        <a
                                            href={buildDriverDetailUrl(driver?.id)}
                                            noWrap
                                            style={{
                                                wordBreak: 'break-word',
                                                overflowWrap: 'break-word',
                                                margin: 0,
                                                flex: 1,
                                                minWidth: 0,
                                                color: '#005bf8',
                                                textDecoration: 'underline',
                                                cursor: 'pointer'
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate(buildDriverDetailUrl(driver?.id));
                                            }}
                                        >
                                            {driver?.id}
                                        </a>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 25 }}>
                                        <strong style={{ marginRight: 5, flexShrink: 0 }}>{messages['driver_performance_events_driver_name']}:</strong><p style={{ wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0, flex: 1, minWidth: 0 }}>{driver?.name}</p>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 25 }}>
                                        <strong style={{ marginRight: 5, flexShrink: 0 }}>{messages['driver_performance_events_driver_supervisor']}:</strong><p style={{ wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0, flex: 1, minWidth: 0 }}>{`${driver?.supervisor?.firstName || ''} ${driver?.supervisor?.lastName || ''}`.trim() || 'N/A'}</p>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', height: 25 }}>
                                        <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_driver_tags']}:</strong>
                                        {
                                            currentTags.length > 0 ? (
                                                <TagsGroup
                                                    options={currentTags || []}
                                                    nameField="label"
                                                    colorField="color"
                                                />
                                            ) : (
                                                <div>N/A</div>
                                            )
                                        }
                                    </div>
                                    {
                                        event && event?.driverId &&
                                        <div>
                                            <Divider sx={{ mt: 1, mb: 1 }} />
                                            <div
                                                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                                                onClick={() => {
                                                    dispatch(openVideoAssignDriverModal({ ...event, unitId: event?.deviceIdGps }))
                                                }}
                                            >
                                                <Icons name="perm_identity" size="small" />
                                                <span className="driver-info-update-driver">
                                                    {messages['driver_performance_events_change_driver']}
                                                </span>
                                            </div>
                                        </div>
                                    }
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 100 }}>{messages['driver_performance_events_driver_no_driver_info']}</div>
                            )
                        }
                    </>
            }
        </div>
    )
}

export default DriverInfo;